import { Injectable } from '@angular/core';
import { FormFieldLocation } from '../classes/form-field-location';
import { isEmpty } from 'lodash';
import { Update } from '@ngrx/entity';
import { CollectionForm } from '../../../../../models/ts/collection-form.model';
import { CollectionFormField } from '../../../../../models/ts/collection-form-field.model';
import { CollectionFormAccordion } from '../../../../../models/ts/collection-form-accordion.model';
import { CollectionFormRow } from '../../../../../models/ts/collection-form-row.model';
import { AccordionStatusType } from '../../../../../models/ts/accordion-status-type.model';
import { StoreCollectionForm, StoreCollectionFormStatus } from '../../../../store/features/forms/forms-state';
import { ViewDataSource } from '../../../../../models/ts/view-data-source.model';
import { CollectionFormFieldGridLookupData } from '../../../../../models/ts/collection-form-field-grid-lookup-data.model';
import { TableFieldDataType } from '../../../../../models/ts/table-field-data-type.model';
import { SchedulerDto } from 'src/models/ts/scheduler-dto.model';
import { FormFieldType } from 'src/models/ts/form-field-type.model';
import { Record } from 'src/models/ts/collection-form-field.model';
import { ViewDataSourcesInstance } from 'src/models/ts/view-data-sources-instance.model';
import { UpdateDeleteState } from 'src/models/ts/update-delete-state.model';
import { OrganizationChartItemType } from 'src/models/ts/organization-chart-item-type.model';
import { FORM_CONSTANTS } from '../constants/form-constants';
import { CollectionLockType } from '../../../../../models/ts/collection-lock-type.model';
import { ViewDataSourceService } from './view-data-source.service';
import { CollectionFormReadOnlyService } from './collection-form-read-only.service';
import { ReadOnlyPriority } from '../enums/read-only-priority.enum';
import { SkillIntegrityCheck } from '../../../../../models/ts/skill-integrity-check.model';
import { fieldDataTypeIsFocusable } from '../functions/field-is-focusable';
import { LinkedCollectionStorageType } from '../../../../../models/ts/linked-collection-storage-type.model';
import { LinkedCollectionType } from '../../../../../models/ts/linked-collection-type.model';
import { LookupItem } from '../../../../../models/ts/collection-list-summary-item.model';
import { LookupService } from '../../../../shared/services/lookup/lookup.service';
import { OrgChartItem } from '../../org-chart/interfaces/org-chart-item';
import { ProtectedFieldType } from '../../../../../models/ts/protected-field-type.model';
import { TrnFieldProperties } from '../../../../../models/ts/trn-field-properties-dto.model';
import moment from 'moment-timezone';
import { ProtectedCollectionType } from '../../../../../models/ts/protected-collection-type.model';
import { ProtectedFieldValueType } from '../../../../../models/ts/protected-field-value-type.model';
import { CollectionFormValidatorResult } from '../classes/collection-form-validator-result';
import { CollectionFormValidatorError } from '../classes/collection-form-validator-error';
import { CollectionFormFieldValue } from '../../../../../models/ts/collection-form-field-value.model';
import { CollectionFieldDto } from 'src/models/ts/collection-field-dto.model';
import { CollectionFormFormulaDto } from 'src/models/ts/collection-form-formulas-tag-dto.model';
import { AiButtonResult } from 'src/app/store/features/forms/effects/forms-ai-effects';
import { TrainingAssessmentDto } from 'src/models/ts/training-assessment-dto.model';

// TODO: separate into multiple files
/**
 * Service that provides various (static) functions for common operations involving CollectionForms and it's fields.
 */
@Injectable({
  providedIn: 'root'
})
export class CollectionFormService {

  public static getFormFieldIdForAiTarget(form: CollectionForm, target: {
    Bookmark: string,
    CollectionFieldsID: number,
    ViewDataSourcesID: number,
    LinkedCollectionName: string,
    LinkedCollectionType: LinkedCollectionType
  }): number {
    if (target.LinkedCollectionType != LinkedCollectionType.GridRecord) {
      let field = CollectionFormService.getField(form, field => field.CollectionFieldsID == target.CollectionFieldsID);
      if (field != undefined)
        return field.Id;
      else throw new Error(`No field found with FieldID ${target.CollectionFieldsID}`);
    } else {
      let grid = CollectionFormService.getField(form, field => CollectionFormService.fieldIsGrid(field) && field.ViewDataSourcesID == target.ViewDataSourcesID);
      if (grid != undefined)
        return grid.Id;
      else throw new Error(`No grid found with VDS ID ${target.ViewDataSourcesID}`);
    }
  }

  public static getAllFormFormulasFieldIds(form: CollectionForm): Array<number> {
    let allFieldIds: number[] = [];
    form.Formulas.forEach(formula => {
      allFieldIds = allFieldIds.concat(CollectionFormService.getAllFormulaFields(formula).map(collectionField => collectionField.CollectionFieldsID));
    });
    return allFieldIds;
  }

  public static getAllFormulaFields(formula: CollectionFormFormulaDto): CollectionFieldDto[] {
    let allFields = formula.Fields.concat(formula.Fields1xN, formula.Fields1x1);
    if (formula.GroupFormulas.length > 0)
      formula.GroupFormulas.forEach(groupFormula => {
        allFields = allFields.concat(CollectionFormService.getAllFormulaFields(groupFormula));
      });
    return allFields;
  }

  public static setFieldReadOnly(form: CollectionForm, fieldId: number, readOnly: boolean, readOnlyPriority: ReadOnlyPriority = ReadOnlyPriority.Design): CollectionForm {
    const field = CollectionFormService.getField(form, f => f.Id === fieldId);
    if (field !== undefined) {
      CollectionFormReadOnlyService.setReadOnly(field, readOnly, readOnlyPriority);
      return form;
    } else {
      throw new Error(`Attempted to modify isReadOnly of field ${fieldId} but field was not found in form.`);
    }
  }

  public static setFieldLoading(form: CollectionForm, fieldId: number, loading: boolean): CollectionForm {
    const field = CollectionFormService.getField(form, f => f.Id === fieldId);
    if (field !== undefined) {
      field.IsLoading = loading;
      return form;
    } else {
      throw new Error(`Attempted to modify IsLoading of field ${fieldId} but field was not found in form.`);
    }
  }

  public static setFieldsLoading(form: CollectionForm, fieldIds: number[], loading: boolean): CollectionForm {
    fieldIds.forEach(fieldId => {
      form = CollectionFormService.setFieldLoading(form, fieldId, loading);
    });
    return form;
  }

  public static setRowHidden(form: CollectionForm, accordionId: number, rowId: number, hide: boolean): CollectionForm {
    let accordion = form.Accordions.find(accordion => accordion.Id == accordionId);
    if (accordion !== undefined) {
      let row = accordion.Rows[rowId];
      if (row !== undefined)
        row.IsHidden = hide;
    }
    return form;
  }

  public static setAccordionHidden(form: CollectionForm, accordionId: number, hide: boolean): CollectionForm {
    let accordion = form.Accordions.find(accordion => accordion.Id == accordionId);
    if (accordion !== undefined) {
      accordion.IsHidden = hide;
    }
    return form;
  }

  public static getLastFieldId(form: CollectionForm): number {
    let visibleFields = CollectionFormService.extractVisibleFieldsFromForm(form);
    return visibleFields.length > 0 ? visibleFields[visibleFields.length - 1].Id : -1;
  }

  /**
   * Sets the given property of field to the provided value.
   * @important This function provides basic type checking (primitives). If the property is a complex type and does not match the type of the provided value, a type error will occur.
   * @param {CollectionForm} form
   * @param {number} fieldId
   * @param {keyof CollectionFormField} property
   * @param value
   * @return {CollectionForm}
   */
  public static setFieldPropertyValue(form: CollectionForm, fieldId: number, property: keyof CollectionFormField, value: unknown): CollectionForm {
    const field = CollectionFormService.getField(form, f => f.Id === fieldId);
    if (field !== undefined) {
      if (typeof field[property] === typeof value) {
        Object.assign(field, { [property]: value });
      } else throw new Error(`Type of property ${property} / ${typeof property} does not match the type of the provided value ${value} / ${typeof value}.`);
      return form;
    } else {
      throw new Error(`Attempted to modify isReadOnly of field ${fieldId} but field was not found in form.`);
    }
  }

  public static setGridFieldReadOnly(form: CollectionForm, gridFieldId: number, recordId: number, fieldId: number, readOnly: boolean, readOnlyPriority: ReadOnlyPriority = ReadOnlyPriority.Design): CollectionForm {
    const record = CollectionFormService.getRecord(form, gridFieldId, recordId);
    if (record !== undefined) {
      const field = CollectionFormService.getRecordField(record, f => f.CollectionFieldsID == fieldId);
      if (field !== undefined) {
        CollectionFormReadOnlyService.setReadOnly(field, readOnly, readOnlyPriority);
        return form;
      } else
        throw new Error(`Attempted to modify isReadOnly of field ${fieldId} but field was not found in form.`);
    } else throw new Error(`Attempted to modify isReadOnly of field ${fieldId} in record ${recordId} but record was not found in grid.`);
  }

  public static setOrgChartValue(form: CollectionForm, fieldId: number, value: Array<OrgChartItem>, ignoreMissingField = false): CollectionForm {
    const field = CollectionFormService.getField(form, f => f.Id === fieldId);
    if (field) {
      field.State = value != field.OrgChartUnitSelector ? UpdateDeleteState.Update : field.State;
      field.OrgChartUnitSelector = value;
      return form;
    } else if (!ignoreMissingField) throw new Error(`Attempted to set value of field ${fieldId} but field was not found in form.`);
    else return form;
  }

  public static setGridOrgChartValue(form: CollectionForm, gridFieldId: number, recordId: number, recordFieldId: number, value: Array<OrgChartItem>, ignoreMissingData = false): CollectionForm {
    const record = CollectionFormService.getRecord(form, gridFieldId, recordId);
    if (record) {
      const field = CollectionFormService.getRecordField(record, field => field.Id == recordFieldId);
      if (field) {
        field.State = value != field.OrgChartUnitSelector ? UpdateDeleteState.Update : field.State;
        field.OrgChartUnitSelector = value;
        return form;
      } else if (!ignoreMissingData) throw new Error(`Attempted to set value of field ${recordFieldId} in record ${recordId} of grid field ${gridFieldId} but field was not found in record.`);
      else return form;
    } else if (!ignoreMissingData) throw new Error(`Attempted to set value of field ${recordFieldId} in record ${recordId} of grid field ${gridFieldId} but record was not found in grid.`);
    else return form;
  }

  public static setRecordEditMode(form: CollectionForm, fieldId: number, recordId: number, editMode: boolean): CollectionForm {
    const record = CollectionFormService.getRecord(form, fieldId, recordId);
    if (record) {
      record.EditMode = editMode;
      // TODO: refactor out the use of LookupService
      LookupService.enableDisableFields(record.Fields, form.ViewDataSources, record.RowDataDesignCrossID ?? record.CrossLinkedInstancesID);
      return form;
    } else {
      console.error(`Could not find record in form with field: ${fieldId}, record: ${recordId}`);
      return form;
    }
  }

  public static setRecordInstanceId(form: CollectionForm, fieldId: number, recordId: number, instanceId: number): CollectionForm {
    const record = CollectionFormService.getRecord(form, fieldId, recordId);
    if (record) {
      record.CrossLinkedInstancesID = instanceId;
      return form;
    } else {
      console.error(`Could not find record in form with field: ${fieldId}, record: ${recordId}`);
      return form;
    }
  }

  public static clearRecordData(form: CollectionForm, fieldId: number, recordId: number): CollectionForm {
    const record = CollectionFormService.getRecord(form, fieldId, recordId);
    const field = CollectionFormService.getField(form, field => field.Id === fieldId);
    const vds = form.ViewDataSources.find(v => v.ViewDataSourcesID == field?.ViewDataSourcesID);

    if (vds?.SingleOrMany == LinkedCollectionType.GridRecord) {
      if (record) {
        const parentDsFields = record?.Fields;
        record.Fields.forEach(rec => {
          if (!rec.IsCrossLinkedField && rec.ViewDataSourcesID == vds.ViewDataSourcesID) {
            rec = this.clearFieldValue(rec);
          }
        });
        LookupService.enableDisableFields(record.Fields, form.ViewDataSources);
      }
      return form;
    } else {
      console.error(`Could not find record in form with field: ${fieldId}, record: ${recordId}`);
      return form;
    }
  }

  public static clearFieldValue(field: CollectionFormField): CollectionFormField {
    switch (field.ComponentType) {
      case TableFieldDataType.HyperLink:
      case TableFieldDataType.AlphaNumeric:
      case TableFieldDataType.RadioGroup:
      case TableFieldDataType.Combobox:
      case TableFieldDataType.Email:
      case TableFieldDataType.SimpleList:
      case TableFieldDataType.TextValue:
      case TableFieldDataType.Memo:
      case TableFieldDataType.Numeric:
      case TableFieldDataType.TimePicker:
      case TableFieldDataType.DateTimePicker:
      case TableFieldDataType.DatePicker:
      case TableFieldDataType.OrganizationChartUnitSelector:
      case TableFieldDataType.EnumList:
        field.OrgChartUnitSelector = [];
        field.Value = '';
        break;
      case TableFieldDataType.ControlledDocument:
        field.Value = '';
        field.Text = '';
        break;
      case TableFieldDataType.Checkbox:
        field.Value = null;
        break;
    }

    if (field.ComponentType == TableFieldDataType.Numeric)
      field.Value = '';

    return field;
  }

  public static setFieldEnumValue(form: CollectionForm, fieldId: number, enumValue: number | undefined): CollectionForm {
    const field = CollectionFormService.getField(form, f => f.Id === fieldId);
    const enumList = field?.EnumList;
    if (field) {
      field.Value = enumList?.find(item => item.Value == enumValue)?.Text ?? '';
      field.State = enumValue != field.EnumValue ? UpdateDeleteState.Update : field.State;
      field.EnumValue = enumValue;
      return form;
    } else return form;
  }

  public static setFieldValue(form: CollectionForm, fieldId: number, value: unknown, ignoreMissingField = false): CollectionForm {
    const field = CollectionFormService.getField(form, f => f.Id === fieldId);
    if (field) {
      field.State = value != field.Value ? UpdateDeleteState.Update : field.State;
      field.Value = value;
      return form;
    } else if (!ignoreMissingField) throw new Error(`Attempted to set value of field ${fieldId} but field was not found in form.`);
    else return form;
  }

  public static setTrainingAssessment(form: CollectionForm, trainingAssessment: TrainingAssessmentDto): CollectionForm {
    const field = CollectionFormService.getField(form, f => f.ProtectedFieldType == ProtectedFieldType.TrainingSessionSubscribedUsers);
    if (field) {
      field.OrgChartUnitSelector.forEach((item: OrgChartItem) => {
        const found = trainingAssessment.Participants.find(p => p.ID == item.TrnUser.ID);
        if (found) {
          item.TrnUser.Present = found.Present;
          item.TrnUser.Remarks = found.Remarks;
          item.TrnUser.SignatureInfo = found.SignatureInfo;
          item.TrnUser.AddToTrainingsQueue = found.AddToTrainingsQueue;
          item.TrnUser.QualificationAchieved = found.QualificationAchieved;
          item.TrnUser.Certificate = found.Certificate;
        }
      });

      field.TrnUsers = field.OrgChartUnitSelector.map(item => item.TrnUser);
    }
    form.TrainingAssessment = trainingAssessment;
    return form;
  }


  public static setFieldSubscriberData(form: CollectionForm, fieldId: number, orgChart: Array<OrgChartItem>, users: Array<TrnFieldProperties>, deletedUsers: Array<TrnFieldProperties>): CollectionForm {
    const field = CollectionFormService.getField(form, f => f.Id === fieldId);
    if (field) {
      field.State = orgChart != field.OrgChartUnitSelector || users != field.TrnUsers || deletedUsers != field.TrnDeletedUsers ? UpdateDeleteState.Update : field.State;
      field.OrgChartUnitSelector = orgChart;
      field.TrnUsers = users;
      field.TrnDeletedUsers = deletedUsers;
      if (form.TrainingAssessment) {
        form.TrainingAssessment.DeletedUsers = deletedUsers?.map(d => d.ID) ?? [];
      }
      return form;
    } else return form;
  }

  public static setFieldCaption(form: CollectionForm, fieldId: number, value: unknown, ignoreMissingField = false): CollectionForm {
    const field = CollectionFormService.getField(form, f => f.Id === fieldId);
    if (field && value) {
      field.Caption = value.toString();
      return form;
    } else if (!ignoreMissingField) throw new Error(`Attempted to set caption of field ${fieldId} but field was not found in form.`);
    else return form;
  }

  public static setAiFieldsReadonly(form: CollectionForm, readOnly: boolean, aiField: CollectionFormField): CollectionForm {
    // filter on FormFieldId (generator of AI button)
    const aiGenerationField = aiField;
    if (aiGenerationField) {
      aiGenerationField.AiTargetFields.concat(aiGenerationField.AiSourceFields).forEach(f => {
        let field = CollectionFormService.getField(form, field => field.CollectionFieldsID === f.CollectionFieldsID);
        if (field === undefined) {
          field = CollectionFormService.getField(form, field => field.ViewDataSourcesID === f.ViewDataSourcesID);
        }
        if (field) {
          if (aiGenerationField.AiTargetFields.includes(f) && !field.IsReadOnly) {
            field.aiGeneration = { aiValue: undefined, generated: false, confirmed: false};
            CollectionFormService.setFieldValue(form, field.Id, undefined, true);
          }
          if (field.aiGeneration || aiGenerationField.AiSourceFields.includes(f)) {
            field.IsReadOnly = readOnly;
            field.ReadOnlyPriority = ReadOnlyPriority.Scripting;
          }
        }
      });
    }
    return form;
  }

  public static clearFormFieldValidation(storeForm: StoreCollectionForm, fieldId: number): StoreCollectionForm {
    if (storeForm.validationResult != undefined) {
      const fieldErrorIndex = storeForm.validationResult.Errors.findIndex(e => e.CollectionFormFieldID == fieldId);
      if (fieldErrorIndex > -1)
        storeForm.validationResult.Errors.splice(fieldErrorIndex, 1);

      // Clear validation if last error is removed
      if (storeForm.validationResult.Errors.length == 0)
        storeForm.validationResult = undefined;
    }
    return storeForm;
  }

  /**
   * Sets AI Generation data on all fields with ai generation
   * @param {CollectionForm} form
   * @param {{aiValue: any, generated: boolean, confirmed: boolean | undefined} | undefined} value
   * @return {CollectionForm}
   */
  public static confirmAiGenerationForForm(form: CollectionForm): CollectionForm {
    let fields = CollectionFormService.extractFieldsFromForm(form).filter(field =>
      field.aiGeneration != undefined && field.aiGeneration.generated
    );
    fields.forEach(field => {
      field.aiGeneration = undefined
    })

    return form;
  }

  public static updateAiGenerationConfirmation(form: StoreCollectionForm): StoreCollectionForm {
    form.hasUnconfirmedAiContent = CollectionFormService.checkFormAiGenerationConfirmation(form.data);
    return form;
  }

  public static setAiUnsavedChanges(form: StoreCollectionForm, unsavedChanges: boolean): StoreCollectionForm {
    form.hasAiUnsavedChanges = unsavedChanges;
    return form;
  }

  /**
   * Loops over fields and checks status of ai generation confirmation. If any fields are marked as generated and not confirmed, returns true otherwise returns false.
   * @param {CollectionForm} form
   * @return {boolean}
   */
  public static checkFormAiGenerationConfirmation(form: CollectionForm): boolean {
    let field;
    for (let i = 0; i < form.Accordions.length; i++) {
      for (let j = 0; j < form.Accordions[i].Rows.length; j++) {
        for (let k = 0; k < form.Accordions[i].Rows[j].Containers.length; k++) {
          for (let l = 0; l < form.Accordions[i].Rows[j].Containers[k].Fields.length; l++) {
            field = form.Accordions[i].Rows[j].Containers[k].Fields[l];
            if (field.aiGeneration != undefined && field.aiGeneration.generated && !field.aiGeneration.confirmed) {
              return true;
            }
          }
        }
      }
    }
    return false;
  }

  public static setFieldAiGeneration(form: CollectionForm, fieldId: number, gridFieldId: number | undefined, recordId: number | undefined, value: {
    aiValue: any,
    generated: boolean,
    confirmed: boolean | undefined,
    aiButtonCollectionFieldId?: number | undefined
  } | undefined, ignoreMissingField = false): CollectionForm {
    if (gridFieldId && recordId) {
      const gridField = CollectionFormService.getField(form, f => f.Id === gridFieldId);
      if (gridField !== undefined) {
        if(value?.confirmed === undefined && value?.aiValue === 'AiButton'){
          gridField.aiGeneration = value;
          return form;
        }else{
          const record = gridField.Records?.find(record => record.RowDataDesignCrossID == recordId);
          if (record !== undefined) {
            const field = CollectionFormService.getRecordField(record, f => f.CollectionFieldsID === fieldId);
            if (field && value) {
              field.aiGeneration = value;
              return form;
            } else if (!ignoreMissingField) throw new Error(`Attempted to set ai generation of field ${fieldId} but field was not found in form.`);
            else return form;
          }
        }
      }
    } else {
      const field = CollectionFormService.getField(form, f => f.Id === fieldId);
      if (field != undefined) {
        field.aiGeneration = value;
        return form;
      } else if (!ignoreMissingField) throw new Error(`Attempted to set ai generation of field ${fieldId} but field was not found in form.`);
      else return form;
    }
    return form;
  }

  public static setFieldAllAiGeneration(form: CollectionForm, fieldsId: number, ignoreMissingField = false): CollectionForm {
    const field = CollectionFormService.getField(form, f => f.CollectionFieldsID === fieldsId);
    if (field) {
      field.AiTargetFields.forEach(field => {
        const foundField = CollectionFormService.getField(form, f => f.CollectionFieldsID === field.CollectionFieldsID);
        if (foundField) {
          if (foundField.aiGeneration) {
            foundField.aiGeneration = undefined
          }
        }
      });
      return form;
    } else if (!ignoreMissingField) throw new Error(`Attempted to set ai generation of all target fields, but failed`);
    else return form;
  }

  public static setFieldAiLoadingInGrid(form: CollectionForm, fieldId: number, gridFieldId: number, recordId: number, aiLoading: boolean | undefined, ignoreMissingField = false): CollectionForm {
    const gridField = CollectionFormService.getField(form, f => f.Id === gridFieldId);
    if (gridField !== undefined) {
      const record = gridField.Records?.find(record => {
        if (record.RowDataDesignCrossID == undefined)
          return record.CrossLinkedInstancesID == recordId;
        else return record.RowDataDesignCrossID == recordId;
      });
      if (record !== undefined) {
        const field = CollectionFormService.getRecordField(record, f => f.CollectionFieldsID === fieldId);
        if (field) {
          // field.AiTargetFields.concat(field.AiSourceFields).forEach(f => {
          //   let field = CollectionFormService.getField(form, field => field.CollectionFieldsID === f.CollectionFieldsID);
          //   if (field === undefined) {
          //     field = CollectionFormService.getField(form, field => field.ViewDataSourcesID === f.ViewDataSourcesID);
          //   }
          //   if (field) {
          //     field.IsReadOnly = false;
          //   }
          // });
          field.AiLoading = aiLoading ?? false;
          return form;
        } else if (!ignoreMissingField) throw new Error(`Attempted to set loading state of ai button field ${fieldId} but field was not found in form.`);
      }
    }
    return form;
  }

  public static setFieldAiLoading(form: CollectionForm, fieldId: number, aiLoading: boolean | undefined, ignoreMissingField = false): CollectionForm {
    const field = CollectionFormService.getField(form, f => f.Id === fieldId);
    if (field) {
      // field.AiTargetFields.concat(field.AiSourceFields).forEach(f => {
      //   let field = CollectionFormService.getField(form, field => field.CollectionFieldsID === f.CollectionFieldsID);
      //   if (field === undefined) {
      //     field = CollectionFormService.getField(form, field => field.ViewDataSourcesID === f.ViewDataSourcesID);
      //   }
      //   if (field) {
      //     field.IsReadOnly = false;
      //   }
      // });
      field.AiLoading = aiLoading ?? false;
      return form;
    } else if (!ignoreMissingField) throw new Error(`Attempted to set loading state of ai button field ${fieldId} but field was not found in form.`);
    else return form;
  }

  public static checkAiFieldConfirmed(form: StoreCollectionForm, formId: string, isAiValidationError: boolean = true): CollectionFormValidatorResult {
    const unConfirmedAiFields: CollectionFormValidatorResult = new CollectionFormValidatorResult();
    const aiFilledFields = CollectionFormService.getFields(form.data, f => f.aiGeneration != undefined);
    const aiBtn = aiFilledFields.find(field => field.aiGeneration?.confirmed === undefined && field.aiGeneration?.aiValue === 'AiButton');


    aiFilledFields.forEach(field => {
      if (field.aiGeneration?.generated && field.aiGeneration?.confirmed != true) {
        const error: CollectionFormValidatorError = {
          Bookmark: field.Caption,
          Message: `AI field (${field.Caption}) is not confirmed`,
          CollectionFormFieldID: field.Id,
          AiValidationError: isAiValidationError
        };
        unConfirmedAiFields.Errors.push(error);
      }
    });

    unConfirmedAiFields.Errors = unConfirmedAiFields.Errors.filter((item, index, self) => self.indexOf(item) === index);
    unConfirmedAiFields.IsValid = unConfirmedAiFields.Errors.length <= 0;
    unConfirmedAiFields.AiButtonId = aiBtn?.aiGeneration?.aiButtonCollectionFieldId ?? 0;
    unConfirmedAiFields.FormId = formId;
    unConfirmedAiFields.IsAiValidation = true;
    return unConfirmedAiFields;
  }

  public static setFirstTimeAiValidation(form: StoreCollectionForm, formId: string, aiButtonResult: AiButtonResult[], aiButtonField: CollectionFormField, isAiValidationError: boolean = true): CollectionFormValidatorResult {
    const validationMessage: CollectionFormValidatorResult = new CollectionFormValidatorResult();
    const aiFilledFields = CollectionFormService.getFields(form.data, f => f.aiGeneration != undefined);

    //Also check fields that are not part of the ai button but have ai generation.
    if (aiFilledFields){
      aiFilledFields.forEach(field => {
        if (field.aiGeneration?.generated && field.aiGeneration?.confirmed != true) {
          const error: CollectionFormValidatorError = {
            Bookmark: field.Caption,
            Message: `AI field (${field.Caption}) is not confirmed`,
            CollectionFormFieldID: field.Id,
            AiValidationError: isAiValidationError
          };
          //if error already exists, do not add again.
          if (!validationMessage.Errors.find(e => e.CollectionFormFieldID == error.CollectionFormFieldID)){
            validationMessage.Errors.push(error);
          }
        }
      });
    }
    aiButtonResult.forEach(field => {
      const fieldFromForm = CollectionFormService.getField(form.data, f => f.Id === field.Key);
      const error: CollectionFormValidatorError = {
        Bookmark: fieldFromForm?.Caption,
        Message: `AI field (${fieldFromForm?.Caption}) is not confirmed`,
        CollectionFormFieldID: fieldFromForm?.Id,
        AiValidationError: isAiValidationError
      };
      //if error already exists, do not add again.
      if (!validationMessage.Errors.find(e => e.CollectionFormFieldID == error.CollectionFormFieldID)){
        validationMessage.Errors.push(error);
      }
    });

    validationMessage.IsValid = validationMessage.Errors.length <= 0;
    validationMessage.AiButtonId = aiButtonField.CollectionFieldsID ?? 0;
    validationMessage.FormId = formId;
    validationMessage.IsAiValidation = true;
    return validationMessage;
  }

  public static setFieldValuePredicate(form: CollectionForm, predicate: (field: CollectionFormField) => boolean, value: unknown, ignoreMissingField = false): CollectionForm {
    const field = CollectionFormService.getField(form, predicate);
    if (field) {
      field.Value = value;
      return form;
    } else if (!ignoreMissingField) throw new Error(`Attempted to set value of field with predicate ${predicate} but field was not found in form.`);
    else return form;
  }

  public static deleteLock(form: CollectionForm): CollectionForm {
    form.ReadMode = false;
    form.IsLocked = false;
    form.LockType = CollectionLockType.Unlocked;
    return form;
  }

  public static setGridFieldValue(form: CollectionForm, gridFieldId: number, recordId: number, recordFieldId: number, value: unknown, ignoreMissingData = false): CollectionForm {
    const record = CollectionFormService.getRecord(form, gridFieldId, recordId);
    if (record) {
      const field = CollectionFormService.getRecordField(record, field => field.CollectionFieldsID == recordFieldId);
      if (field) {
        field.State = value != field.Value ? UpdateDeleteState.Update : field.State;
        field.Value = value;
        return form;
      } else if (!ignoreMissingData) throw new Error(`Attempted to set value of field ${recordFieldId} in record ${recordId} of grid field ${gridFieldId} but field was not found in record.`);
      else return form;
    } else if (!ignoreMissingData) throw new Error(`Attempted to set value of field ${recordFieldId} in record ${recordId} of grid field ${gridFieldId} but record was not found in grid.`);
    else return form;
  }

  public static setGridFieldRecords(form: CollectionForm, gridFieldId: number, records: Record[]): CollectionForm {
    const gridField = CollectionFormService.getField(form, field => field.Id == gridFieldId);
    if (gridField !== undefined) {
      gridField.Records = records;
      return form;
    } else return form;
  }

  public static setGridFieldOrgChartValue(form: CollectionForm, gridFieldId: number, recordId: number, recordFieldId: number, value: Array<OrgChartItem>, ignoreMissingData = false): CollectionForm {
    const record = CollectionFormService.getRecord(form, gridFieldId, recordId);
    if (record) {
      const field = CollectionFormService.getRecordField(record, field => field.CollectionFieldsID == recordFieldId);
      if (field) {
        field.State = value != field.OrgChartUnitSelector ? UpdateDeleteState.Update : field.State;
        field.OrgChartUnitSelector = value;
        return form;
      } else if (!ignoreMissingData) throw new Error(`Attempted to set value of field ${recordFieldId} in record ${recordId} of grid field ${gridFieldId} but field was not found in record.`);
      else return form;
    } else if (!ignoreMissingData) throw new Error(`Attempted to set value of field ${recordFieldId} in record ${recordId} of grid field ${gridFieldId} but record was not found in grid.`);
    else return form;
  }

  public static setListFieldValue(form: CollectionForm, fieldId: number, value: Record[], ignoreMissingField = false): CollectionForm {
    const field = CollectionFormService.getField(form, f => f.Id === fieldId);
    if (field) {
      field.State = value != field.Records ? UpdateDeleteState.Update : field.State;
      field.Records = value;
      return form;
    } else if (!ignoreMissingField) throw new Error(`Attempted to set value of field ${fieldId} but field was not found in form.`);
    else return form;
  }

  public static updateRecordData(form: CollectionForm, gridFieldId: number, recordId: number, lookupData: CollectionFormFieldGridLookupData): CollectionForm {
    const record = CollectionFormService.getRecord(form, gridFieldId, recordId);
    if (record) {
      lookupData = structuredClone(lookupData);
      LookupService.enableDisableFields(lookupData.FormFields, form.ViewDataSources, record.RowDataDesignCrossID ?? record.CrossLinkedInstancesID);
      const dataFields = new Map(lookupData.FormFields.map(field => [field.CollectionFieldsID, field]));
      record.Fields = record.Fields.map(recordField => {
        return dataFields.get(recordField.CollectionFieldsID) ?? recordField;
      });
      return form;
    } else return form;
  }

  public static updateGridRecord(form: CollectionForm, viewDatasourceId: number, lookupData: CollectionFormFieldGridLookupData, recordId: number, newRecordId?: number): CollectionForm {
    let gridField = CollectionFormService.getField(form, field => field.ViewDataSourcesID == viewDatasourceId && CollectionFormService.fieldIsGrid(field));
    if (gridField !== undefined) {
      if (newRecordId !== undefined && newRecordId !== recordId) {
        CollectionFormService.setRecordInstanceId(form, gridField.Id, recordId, newRecordId);
      }
      CollectionFormService.updateRecordData(form, gridField.Id, newRecordId ?? recordId, lookupData);
    }
    return form;
  }

  public static updateRecordWithLookup(form: CollectionForm, gridFieldId: number, recordId: number, lookupData: CollectionFormFieldGridLookupData, lookupItem: LookupItem): CollectionForm {
    const record = CollectionFormService.getRecord(form, gridFieldId, recordId);
    if (record) {
      const field = CollectionFormService.getField(form, f => f.Id === gridFieldId);
      const vds = form.ViewDataSources.find(v => v.ViewDataSourcesID == field?.ViewDataSourcesID);


      if (vds) {
        if (vds?.ParentDataSourcesID > 0 && lookupData.ViewDataSources != undefined && lookupData.ViewDataSources.length > 0) {
          form = CollectionFormService.updateViewDataSources(form, lookupData.ViewDataSources);
          return CollectionFormService.updateRecordData(form, gridFieldId, recordId, lookupData);
        }
        const instances = vds.Instances.filter(i => i.CrossLinkedInstancesID == recordId);
        if (instances) {
          instances.forEach(i => {
            i.State = UpdateDeleteState.Delete;
          });
          if (recordId < 0) {
            vds.Instances = vds.Instances.filter(i => i.CrossLinkedInstancesID != recordId);
          }
          vds.Instances = vds.Instances.filter(i => ((i.isNew ?? false) !== true || i.CrossLinkedInstancesID != recordId));
          vds.Instances.push({
            CrossLinkedInstancesID: recordId,
            ChildInstancesID: lookupItem.InstancesID,
            OriginalChildInstancesID: lookupItem.OriginalChildInstancesID ?? lookupItem.InstancesID,
            ChildVersionsID: lookupItem.VersionsID,
            ParentInstancesID: lookupItem.InstancesID,
            ParentVersionsID: lookupItem.VersionsID,
            State: UpdateDeleteState.Update,
            isNew: true
          } as ViewDataSourcesInstance);
          return CollectionFormService.updateRecordData(form, gridFieldId, recordId, lookupData);
        }
      }


      //instanceChange.ChildInstancesID =
      return CollectionFormService.updateRecordData(form, gridFieldId, recordId, lookupData);
    } else return form;
  }

  public static fillRecordWithLookupData(record: Record, lookupData: CollectionFormFieldGridLookupData, instanceId: number): Record {
    const dataFields = new Map(lookupData.FormFields.map(field => [field.CollectionFieldsID, field]));
    record.Fields = record.Fields.map(recordField => {
      return dataFields.get(recordField.CollectionFieldsID) ?? recordField;
    });
    record.CrossLinkedInstancesID = instanceId;
    record.RowDataDesignCrossID = instanceId;
    record.EditMode = false;
    return record;
  }

  public static updateViewDataSource(form: CollectionForm, viewDataSource: ViewDataSource): CollectionForm {
    const vdsIndex = form.ViewDataSources.findIndex(v => v.ViewDataSourcesID === viewDataSource.ViewDataSourcesID);
    if (vdsIndex != -1) {
      form.ViewDataSources[vdsIndex].Instances = viewDataSource.Instances;
    }
    return form;
  }

  public static updateViewDataSources(form: CollectionForm, viewDataSources: ViewDataSource[]): CollectionForm {
    viewDataSources.forEach(vds => {
      form = CollectionFormService.updateViewDataSource(form, vds);
    });
    return form;
  }

  public static addRecords(form: CollectionForm, gridFieldId: number, records: Record[]): CollectionForm {
    records = structuredClone(records);
    records.forEach(record => {
      LookupService.enableDisableFields(record.Fields, form.ViewDataSources, record.RowDataDesignCrossID ?? record.CrossLinkedInstancesID);
    });
    const gridField = this.getField(form, field => field.Id == gridFieldId);
    if (gridField !== undefined && gridField.Records !== undefined) {
      gridField.Records = gridField.Records.concat(records);
      return form;
    } else return form;
  }

  // TODO: merge ViewDataSourceService.addRecordInstancesToViewDataSource into this
  public static addRecord(form: CollectionForm, gridFieldId: number, record: Record): CollectionForm {
    const gridField = this.getField(form, field => field.Id == gridFieldId);
    if (gridField !== undefined && gridField.Records !== undefined) {
      gridField.Records.push(record);
      return form;
    } else return form;
  }

  public static removeRecord(form: CollectionForm, gridFieldId: number, recordId: number): CollectionForm {
    const gridField = CollectionFormService.getField(form, f => f.Id === gridFieldId);
    if (gridField && gridField.Records && gridField.Records.length > 0) {

      const record = CollectionFormService.getRecordFromField(gridField, record => {
        return record.CrossLinkedInstancesID === recordId;
      });
      if (record) {
        if (record.CrossLinkedInstancesID < 0) {
          gridField.Records.splice(gridField.Records.findIndex(r => r.CrossLinkedInstancesID == recordId), 1);
          const vds = form.ViewDataSources.find(v => v.ViewDataSourcesID === gridField.ViewDataSourcesID);
          if (vds != undefined) {
            vds.Instances = vds.Instances?.filter(instance => instance.CrossLinkedInstancesID != recordId);
          }
        } else {
          record.State = UpdateDeleteState.Delete;

          const vds = form.ViewDataSources.find(v => v.ViewDataSourcesID === gridField.ViewDataSourcesID);
          if (vds != undefined) {
            const instance =
              CollectionFormService.getViewDataSourceInstanceByCrossLinkedInstanceId(vds?.Instances, recordId);
            if (instance != undefined) {
              instance.State = UpdateDeleteState.Delete;

              // Attempt to clear the parent cross instances too
              const parentCross = form.ViewDataSources.find(v =>
                v.DataDesignViewDataSourcesID == vds.ViewDataSourcesID && v.IsSnapshotRelation);
              if (parentCross != undefined) {
                const parentCrossInstance = parentCross.Instances.find(i => i.RowDataDesignCrossID == instance.RowDataDesignCrossID);
                if (parentCrossInstance != undefined) {
                  parentCross.Instances.splice(parentCross.Instances.indexOf(parentCrossInstance), 1);
                  //
                  // if (parentCrossInstance.CrossLinkedInstancesID < 0)
                  //   parentCross.Instances.splice(parentCross.Instances.indexOf(parentCrossInstance), 1);
                  // else
                  //   parentCrossInstance.State = UpdateDeleteState.Delete;
                }
              }
            }
          }

        }
      }
      return form;
    } else return form;
  }

  /**
   * Removes multiple records from a grid field in a collection form.
   * This function will handle both negative (unsaved) and positive (saved) record IDs appropriately.
   *
   * @param {CollectionForm} form - The collection form containing the grid field.
   * @param {number} gridFieldId - The ID of the grid field containing the records.
   * @param {number[]} recordIds - Array of record IDs to be removed.
   * @returns {CollectionForm} The updated collection form.
   */
  public static removeRecords(form: CollectionForm, gridFieldId: number, recordIds: number[]): CollectionForm {
    if (!recordIds || recordIds.length === 0) {
      return form;
    }

    const gridField = CollectionFormService.getField(form, f => f.Id === gridFieldId);
    if (!(gridField && gridField.Records && gridField.Records.length > 0)) {
      return form;
    }

    const vds = form.ViewDataSources.find(v => v.ViewDataSourcesID === gridField.ViewDataSourcesID);
    if (!vds) {
      return form;
    }

    // Handle each record ID
    recordIds.forEach(recordId => {
      const record = CollectionFormService.getRecordFromField(gridField, record => {
        return record.CrossLinkedInstancesID === recordId;
      });

      if (record) {
        if (record.CrossLinkedInstancesID < 0 && gridField.Records) {
          // For negative IDs (unsaved records), remove them completely
          const recordIndex = gridField.Records.findIndex(r => r.CrossLinkedInstancesID == recordId);
          if (recordIndex >= 0) {
            gridField.Records.splice(recordIndex, 1);
          }

          // Filter out from instances
          if (vds.Instances) {
            vds.Instances = vds.Instances.filter(instance => instance.CrossLinkedInstancesID != recordId);
          }
        } else {
          // For positive IDs (saved records), mark as deleted
          record.State = UpdateDeleteState.Delete;

          // Find and update the instance state
          const instance = CollectionFormService.getViewDataSourceInstanceByCrossLinkedInstanceId(vds.Instances, recordId);
          if (instance) {
            instance.State = UpdateDeleteState.Delete;

            // Handle parent cross instances
            const parentCross = form.ViewDataSources.find(v =>
              v.DataDesignViewDataSourcesID == vds.ViewDataSourcesID && v.IsSnapshotRelation);
            if (parentCross) {
              const parentCrossInstance = parentCross.Instances.find(i => i.RowDataDesignCrossID == instance.RowDataDesignCrossID);
              if (parentCrossInstance) {
                parentCross.Instances.splice(parentCross.Instances.indexOf(parentCrossInstance), 1);
              }
            }
          }
        }
      }
    });

    return form;
  }

  public static clearGrid(form: CollectionForm, gridFieldId: number): CollectionForm {
    let gridField = CollectionFormService.getField(form, field => field.Id == gridFieldId);
    if (gridField !== undefined) {
      gridField.Records = [];
    }
    return form;
  }

  /**
   * Returns a flat array of all fields in provided form.
   * @param form
   */
  public static extractFieldsFromForm(form: CollectionForm): CollectionFormField[] {
    let fields: CollectionFormField[] = [];
    form.Accordions.forEach((accordion) => {
      fields = fields.concat(CollectionFormService.extractFieldsFromAccordion(accordion));
    });
    return fields;
  }

  public static extractVisibleFieldsFromForm(form: CollectionForm): CollectionFormField[] {
    let fields: CollectionFormField[] = [];
    form.Accordions.filter(accordion => accordion.IsHidden !== true).forEach((accordion) => {
      accordion.Rows.filter(row => row.IsHidden !== true).forEach((row, rowIndex) => {
        row.Containers.forEach((container) => {
          fields = fields.concat(container.Fields.filter(field => field.IsHidden !== true));
        });
      });
    });
    return fields;
  }

  public static extractRecordsByVds(form: CollectionForm, viewDataSource: ViewDataSource): Record[] {
    let gridField = CollectionFormService.getField(form, field => field.ViewDataSourcesID == viewDataSource.ViewDataSourcesID && CollectionFormService.fieldIsGrid(field));
    if (gridField !== undefined) {
      return gridField.Records ?? [];
    } else throw new Error(`No grid field found associated with VDS ${viewDataSource.ViewDataSourcesID}`);
  }

  /**
   * Returns the first focusable field in the provided form.
   * @param form
   */
  public static getFirstFocusableFieldFromForm(form: CollectionForm): CollectionFormField | undefined {
    //Conditions:
    // Is a focusable field => accept a TableFieldDataType
    // Is NOT Readonly
    // Is NOT Hidden (by scripting =>  isHidden)
    return CollectionFormService.extractFieldsFromForm(form).find((f: CollectionFormField) => {
        return !form.IsLocked && !CollectionFormService.fieldIsGrid(f) &&
          fieldDataTypeIsFocusable(f.ComponentType) &&
          (!f.IsReadOnly) &&
          (!f.IsHidden);
      }
    );
  }

  /**
   * Returns a flat array of all fields in provided accordion.
   * @param accordion
   */
  public static extractFieldsFromAccordion(accordion: CollectionFormAccordion): CollectionFormField[] {
    let fields: CollectionFormField[] = [];
    accordion.Rows.forEach((row) => {
      fields = fields.concat(CollectionFormService.extractFieldsFromRow(row));
    });
    return fields;
  }

  /**
   * Returns a flat array of all fields in provided row.
   * @param row
   */
  public static extractFieldsFromRow(row: CollectionFormRow): CollectionFormField[] {
    const fields: CollectionFormField[] = [];
    row.Containers.forEach((container) => {
      container.Fields.forEach((field) => {
        // Ignore non collection fields => UI elements and lists are not part of the form
        if (field.Bookmark != null && field.Bookmark.toLowerCase() == 'mailinglist' && field.Value != null && field.Value != '') {
          fields.push(field);
        }
        if (field.CollectionFieldsID != 0 || field.ListSourceCollectionsID !== 0)
          fields.push(field);
      });
    });
    return fields;
  }

  /**
   * Toggles the given accordion and returns the updated form.
   * The original form is not modified. (immutable)
   * @param form
   * @param accordionId
   */
  public static toggleFormAccordion(form: CollectionForm, accordionId: number): CollectionForm {
    if (!form) {
      throw new Error(`Form is undefined`);
    } else {
      const index = form.Accordions.findIndex((a) => a.Id == accordionId);
      if (index >= 0) {
        const updatedForm = structuredClone(form);
        updatedForm.Accordions[index].AccordionStatus =
          updatedForm.Accordions[index].AccordionStatus == AccordionStatusType.Open ? AccordionStatusType.Closed : AccordionStatusType.Open;
        return updatedForm;
      } else {
        throw new Error(
          `Accordion with ID ${accordionId} not found in form ${form.CollectionsID}`
        );
      }
    }
  }

  /**
   * Returns the location object of the given fieldId in the provided form.
   * If the field is not found, returns undefined.
   * @param {CollectionForm} form
   * @param {number} fieldId
   * @return {FormFieldLocation | undefined}
   */
  public static getLocationByFieldId(form: CollectionForm, fieldId: number): FormFieldLocation | undefined {
    let location: FormFieldLocation | undefined = undefined;
    form.Accordions.forEach((accordion) => {
      accordion.Rows.forEach((row, rowIndex) => {
        row.Containers.forEach((container) => {
          container.Fields.forEach((field) => {
            if (field.Id == fieldId) {
              location = new FormFieldLocation(accordion.Id, rowIndex, container.Id, field.Id);
            }
          });
        });
      });
    });
    return location;
  }

  /**
   * Returns the field of the given form that matches the provided predicate.
   * @param {CollectionForm} form
   * @param {(field: CollectionFormField) => boolean} predicate
   * @return {CollectionFormField | undefined}
   */
  public static getField(form: CollectionForm, predicate: (field: CollectionFormField) => boolean): CollectionFormField | undefined {
    for (let a = 0; a < form.Accordions.length; a++) {
      const accordion = form.Accordions[a];
      for (let r = 0; r < accordion.Rows.length; r++) {
        const row = accordion.Rows[r];
        for (let c = 0; c < row.Containers.length; c++) {
          const container = row.Containers[c];
          for (let f = 0; f < container.Fields.length; f++) {
            const field = container.Fields[f];
            if (predicate(field)) {
              return field;
            }
          }
        }
      }
    }
    return undefined;
  }

  public static getRecordField(record: Record, predicate: (field: CollectionFormField) => boolean): CollectionFormField | undefined {
    return record.Fields.find(predicate);
  }

  public static getRecordFromField(field: CollectionFormField, predicate: (record: Record) => boolean): Record | undefined {
    if (field.Records)
      return field.Records.find(predicate);
    else return undefined;
  }

  /**
   * Gets a record from given field by ID.
   * First checks for RowDataDesignCrossID, but falls back on CrossLinkedInstancesID if the former is not present.
   * @param {CollectionForm} form
   * @param {number} fieldId
   * @param {number} recordId
   * @return {Record | undefined}
   */
  public static getRecord(form: CollectionForm, fieldId: number, recordId: number): Record | undefined {
    const field = CollectionFormService.getField(form, field => {
      return field.Id == fieldId;
    });
    if (field)
      return CollectionFormService.getRecordFromField(field, record => {
        return (record.RowDataDesignCrossID ?? record.CrossLinkedInstancesID) == recordId;
      });
    else return undefined;
  }

  public static getFields(form: CollectionForm, predicate: (field: CollectionFormField) => boolean): Array<CollectionFormField> {
    const fields = CollectionFormService.extractFieldsFromForm(form);
    return fields.filter(field => predicate(field));
  }

  public static setLoading(storeForm: StoreCollectionForm, loading: boolean): Update<StoreCollectionForm> {
    const updatedForm = structuredClone(storeForm);
    updatedForm.loading = loading;
    return { id: storeForm.id, changes: updatedForm };
  }

  /**
   * Maps a form to a StoreCollectionForm. Useful for NgRx reducers.
   * @param form
   * @param id
   */
  public static toStoreCollectionForm(form: CollectionForm, id: string, status: StoreCollectionFormStatus): StoreCollectionForm {
    return { id, data: form, status };
  }

  /**
   * Maps a form to an Update for NgRx Reducer.
   * @param form
   * @param id
   * @param status
   */
  public static toStoreUpdate(form: CollectionForm, id: string, status: StoreCollectionFormStatus): Update<StoreCollectionForm> {
    return { id, changes: CollectionFormService.toStoreCollectionForm(form, id, status) };
  }

  /**
   * Maps a form to an Update for NgRx Reducer.
   * @param form
   * @param id
   */
  public static toStoreUpdateScheduler(form: CollectionForm, id: string, scheduler: SchedulerDto, status: StoreCollectionFormStatus): Update<StoreCollectionForm> {
    return { id, changes: { id, data: form, schedulerData: scheduler, status } };
  }

  /**
   * @param {CollectionForm} form
   * @param {string} id
   * @param {Map<number, SkillIntegrityCheck> | undefined} integrity
   * @returns {Update<StoreCollectionForm>}
   */
  public static toStoreUpdateIntegrity(form: CollectionForm, id: string, integrity: Map<number, SkillIntegrityCheck> | undefined, status: StoreCollectionFormStatus): Update<StoreCollectionForm> {
    let value: Array<{ fieldId: number, value: SkillIntegrityCheck }> | undefined = undefined;
    if (integrity != undefined) {
      value = Array.from(integrity, ([fieldId, value]) => ({ fieldId, value }));
    }
    return { id, changes: { id, data: form, integrity: value, status } };
  }

  public static updateFieldOptions(form: CollectionForm, id: string, fieldId: number, gridFieldId: number, options: Array<CollectionFormFieldValue>): Update<StoreCollectionForm> {
    const field = CollectionFormService.getField(form, f => f.Id === gridFieldId);
    if (field) {
      field.Records?.forEach(record => {
        const gridField = record.Fields.find(f => f.CollectionFieldsID === fieldId);
        if (gridField) {
          gridField.FieldValues = options;
        }
      });
      return { id: id, changes: { id: id, data: form } };
    } else return { id: id, changes: { id: id, data: form } };
  }

  public static updateTrainingDocumentFields(form: CollectionForm, id: string): {
    hasUpdates: boolean,
    update: Update<StoreCollectionForm>
  } {
    if (form?.ViewDataSources) {
      let hasChanges = false;
      const trnDocRelation = form.ViewDataSources.find(vds => vds.ProtectedCollectionType == ProtectedCollectionType.LinkTrainingDocument);
      if (trnDocRelation != undefined) {
        const trainingTypeField = this.getField(form, (f => f.ProtectedFieldType == ProtectedFieldType.TrainingType));
        let relatedFields = this.getFields(form, f => f.ViewDataSourcesID == trnDocRelation.ViewDataSourcesID);
        if (trnDocRelation.Instances != undefined && trnDocRelation.Instances.length > 0) {
          // disable fields
          relatedFields = relatedFields.filter(f => f.IsReadOnly != true);
          relatedFields.forEach(el => el.IsReadOnly = true);
          if (trainingTypeField) {
            const collectionFieldValuesId = trainingTypeField.FieldValues.find(fv => fv.ProtectedFieldValueType == ProtectedFieldValueType.DocumentTraining)?.CollectionFieldValuesID;
            if (trainingTypeField.Value != collectionFieldValuesId) {
              hasChanges = true;
              trainingTypeField.Value = collectionFieldValuesId;
            }


            const caption = trainingTypeField.FieldValues.find(fv => fv.ProtectedFieldValueType == ProtectedFieldValueType.DocumentTraining)?.Caption ?? '';
            if (trainingTypeField.Text != caption) {
              hasChanges = true;
              trainingTypeField.Text = caption;
            }
          }
        } else {
          //hide fields
          relatedFields = relatedFields.filter(f => f.IsHidden != true);
          relatedFields.forEach(el => el.IsHidden = true);
        }
        hasChanges = hasChanges || relatedFields.length > 0;
        return { hasUpdates: hasChanges, update: { id: id, changes: { id: id, data: form } } };
      }
    }

    return { hasUpdates: false, update: { id: id, changes: { id: id, data: form } } };
  }

  /**
   * Returns a JSON string representation of the given form.
   * @param form
   * @deprecated
   */
  public static stringify(form: CollectionForm): string {
    // Flat array of all fields
    const fields = CollectionFormService.extractFieldsFromForm(form);

    // Fields belonging to the form itself
    const baseFields = fields.filter(f => f.ViewDataSourcesID == 0 && f.Bookmark != null);

    // Output to stringify
    const jsonObject: { [key: string]: unknown } = {};

    if (form.VersionsID > 0)
      jsonObject['VersionsID'] = form.VersionsID;
    jsonObject['DocumentProperties'] = form.DocumentProperties;

    // Base form fields
    baseFields.forEach((field) => {
        const value = CollectionFormService.getValueForField(field);
        if (value != null) {
          Object.assign(jsonObject, { [field.Bookmark]: value });
        }

      }
    );

    // Relations
    if (form.ViewDataSources && form.ViewDataSources.length > 0) {
      const formVds = structuredClone(form.ViewDataSources);
      const nonSnapshotDataSources = formVds.filter(vds => vds.IsSnapshotRelation == false);
      const linkedFields = structuredClone(fields.filter(f => f.ViewDataSourcesID > 0));
      ViewDataSourceService.addLinkedCollectionFieldsToJson(jsonObject, 0, nonSnapshotDataSources, linkedFields, formVds);
    }

    // Fixed fields
    jsonObject['DocumentProperties'] = form.DocumentProperties;
    jsonObject['InheritType'] = form.InheritType;
    jsonObject['DisplayFieldsInheritType'] = form.DisplayFieldsInheritType;
    jsonObject['ExamSubscriptions'] = form.ExamSubscriptions;
    jsonObject['TrainingAssessment'] = form.TrainingAssessment;

    return CollectionFormService.stringifyFormJson(jsonObject);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static getValueForField(field: CollectionFormField): Object | null {
    switch (field.ComponentType) {
      case TableFieldDataType.AlphaNumeric: {
        if (field.Value !== undefined)
          return { value: field.Value == null ? null : field.Value.trim() };
        else return null;
      }
      case TableFieldDataType.OrganizationChartUnitSelector:
        return CollectionFormService.getOrgChartArrayForJson(field.OrgChartUnitSelector);
      case TableFieldDataType.OrganizationChartUnitSelectorForTrnApp:
        return field.TrnUsers ?? [];
      case TableFieldDataType.RadioGroup:
      case TableFieldDataType.Combobox:
        return (isNaN(field.Value) || field.Value == 0) ? null : { value: field.Value };
      case TableFieldDataType.EnumList:
        return { value: field.EnumValue ?? null };
      case TableFieldDataType.Memo:
        return { value: isEmpty(field.Value) ? null : field.Value };
      case TableFieldDataType.Numeric:
        return { value: Number.isNaN(field.Value) || field.Value == Infinity || field.Value == undefined ? null : field.Value };
      case TableFieldDataType.DatePicker : {
        return { value: CollectionFormService.isEmpty(field.Value) ? null : CollectionFormService.convertDateTimeToDate(new Date(field.Value)) ?? null };
      }
      case TableFieldDataType.DateTimePicker : {
        return { value: CollectionFormService.isEmpty(field.Value) ? null : field.Value ?? null };
      }
      default:
        if (field.Value !== undefined)
          return { value: field.Value };
        else return null;
    }
  }

  public static isEmpty(value: unknown): boolean {
    //isEmpty returns true when value is an object..
    return !value || (isEmpty(value) && typeof value !== 'object');
  }

  public static convertDateTimeToUtcTimeZone(dateTime: Date | undefined): Date | undefined {
    if (dateTime == undefined) {
      return undefined;
    }
    return moment([dateTime.getFullYear(), dateTime.getMonth(), dateTime.getDate(), dateTime.getHours(), dateTime.getMinutes(), 0, 0]).toDate();
  }

  public static convertUtcToDateTimeTimeZone(dateTime: Date | undefined): Date | undefined {
    if (dateTime == undefined) {
      return undefined;
    }
    const fixed = moment.utc([dateTime.getFullYear(), dateTime.getMonth(), dateTime.getDate(), dateTime.getHours(), dateTime.getMinutes(), 0, 0]);
    const date = fixed.toDate();
    return date;
  }

  public static correctDateTimeToCorrectTimeZone(dateTime: Date | undefined): Date | undefined {
    if (dateTime == undefined) {
      return undefined;
    }
    return moment([dateTime.getFullYear(), dateTime.getMonth(), dateTime.getDate(), dateTime.getHours(), dateTime.getMinutes(), 0, 0]).toDate();
  }

  public static convertDateTimeToDate(dateTime: Date | undefined): string | undefined {
    if (dateTime == undefined || isNaN(dateTime.getTime())) return '';
    const year = dateTime.getFullYear();
    const month = (dateTime.getMonth() + 1).toString().padStart(2, '0');
    const day = dateTime.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00+00:00`;
  }

  public static getOrgChartArrayForJson(list: OrgChartItem[]): {
    type: OrganizationChartItemType,
    value: number,
    text: string
  }[] {
    const array: { type: OrganizationChartItemType, value: number, text: string }[] = [];
    list.forEach(val => {
      array.push({
        type: val.ItemType,
        value: val.VersionId,
        text: val.Name
      });
    });
    return array;
  }

  public static getFieldsWithValue(fields: CollectionFormField[]): CollectionFormField[] {
    return fields.filter((field) => {
      return ((field.Value != undefined && field.Value != null && field.Value !== '')
          || field.State == UpdateDeleteState.Update)
        || field.OrgChartUnitSelector.length > 0
        || (field.IsHidden || field.IsRequired || field.Evaluations.length > 0)
        || (field.Bookmark != null && field.Bookmark.toLowerCase() == 'mailinglist');
    });
  }

  /**
   * Get the ViewDataSource linked to a field
   * @param {CollectionForm} form
   * @param {CollectionFormField} field
   * @return {ViewDataSource | undefined}
   */
  public static getViewDataSourceForField(form: CollectionForm, field: CollectionFormField): ViewDataSource | undefined {
    return form.ViewDataSources.find(v => v.ViewDataSourcesID === field.ViewDataSourcesID);
  }

  public static getViewDataSourceByCrossLinkId(form: CollectionForm, crossLinkCollectionId: number): ViewDataSource | undefined {
    return form.ViewDataSources.find(v => v.CrossLinkCollectionsID === crossLinkCollectionId);
  }

  /**
   * Get the ViewDataSource linked to a field by id
   * @param {CollectionForm} form
   * @param {number} fieldId
   * @return {ViewDataSource | undefined}
   */
  public static getViewDataSourceForFieldById(form: CollectionForm, fieldId: number): ViewDataSource | undefined {
    const vds: ViewDataSource | undefined = undefined;
    const field = CollectionFormService.getField(form, f => {
      return f.Id === fieldId;
    });
    if (field) {
      return CollectionFormService.getViewDataSourceForField(form, field);
    }
    return vds;
  }

  public static getViewDataSourceInstanceByCrossLinkedInstanceId
  (instances: ViewDataSourcesInstance[], crossLinkedInstancesId: number): ViewDataSourcesInstance | undefined {
    return instances.find(i => i.CrossLinkedInstancesID === crossLinkedInstancesId);
  }

  /**
   * Returns a boolean indicating if a linked field should be readonly.
   * @param {CollectionFormField} field
   * @return {boolean}
   */
  // TODO: optimize so the hasParent instance is called only once by bulking fields
  public static isLinkedFieldReadOnly(form: CollectionForm, field: CollectionFormField): boolean {
    if (CollectionFormService.fieldIsGrid(field)) return CollectionFormService.isLinkedGridReadOnly(form, field);
    const vds = form.ViewDataSources.find(v => v.ViewDataSourcesID === field.ViewDataSourcesID);
    if (vds) {
      if (vds.EntityRelatedFieldID !== null && field.ProtectedFieldType == ProtectedFieldType.DepartmentName) {
        let entityDependencyOrgChartField = CollectionFormService.getField(form, field => field.CollectionFieldsID == vds.EntityRelatedFieldID);
        if (entityDependencyOrgChartField !== undefined) {
          return entityDependencyOrgChartField.OrgChartUnitSelector.length == 0;
        } else return true;
      }
      if (vds.ParentDataSourcesID !== 0) {
        if (vds.LinkedCollectionStorageType == LinkedCollectionStorageType.Relational || (vds.LinkedCollectionStorageType == LinkedCollectionStorageType.Historical && field.MustBeInList)) {
          if (field.IsLookupField || field.ComponentType == TableFieldDataType.ControlledDocument || CollectionFormService.fieldIsGrid(field)) {
            // Lookup, Relational, editable if parents have instances
            return !ViewDataSourceService.viewDataSourceHasParentInstances(form.ViewDataSources, field.ViewDataSourcesID);
          } else {
            // Non lookup, Relational, only editable if crosslink field and parents have instances
            return !(ViewDataSourceService.viewDataSourceHasParentInstances(form.ViewDataSources, field.ViewDataSourcesID) && field.IsCrossLinkedField);
          }
        } else {
          if (field.IsLookupField || field.ComponentType == TableFieldDataType.ControlledDocument) {
            // Lookup, Historical, editable if parents have instances
            return !ViewDataSourceService.viewDataSourceHasParentInstances(form.ViewDataSources, field.ViewDataSourcesID);
          } else {
            // Non lookup, Historical, editable if an instance is selected
            return !ViewDataSourceService.viewDataSourceHasInstance(form.ViewDataSources, field.ViewDataSourcesID);
          }
        }
      } else {
        if (field.IsLookupField || (field.ComponentType == TableFieldDataType.ControlledDocument && field.ViewDataSourcesID != 0) || CollectionFormService.fieldIsGrid(field)) {
          // Lookup, Top level => always editable
          return false;
        } else {
          // Non lookup, only editable if Historical/CrossLink and instance selected
          return !((ViewDataSourceService.viewDataSourceHasInstance(form.ViewDataSources, field.ViewDataSourcesID) && ((vds.LinkedCollectionStorageType == LinkedCollectionStorageType.Historical) || field.IsCrossLinkedField)));
        }
      }
    } else return true;
  }

  public static isLinkedGridReadOnly(form: CollectionForm, field: CollectionFormField): boolean {
    const vds = form.ViewDataSources.find(v => v.ViewDataSourcesID === field.ViewDataSourcesID);
    if (vds) {
      // TODO: double check if Reversed grids are ever editable
      if (vds.IsReversedRelation) return true;
      if (vds.ParentDataSourcesID !== 0) {
        // 1x...xN, editable if parent has VDS
        return !ViewDataSourceService.viewDataSourceHasParentInstances(form.ViewDataSources, field.ViewDataSourcesID);
      } else {
        // 1xN, editable
        return false;
      }
    } else return true;
  }

  public static fieldIsGrid(field: CollectionFormField): boolean {
    return field.FormFieldType == FormFieldType.List ||
      field.FormFieldType == FormFieldType.LinkedCollection ||
      field.FormFieldType == FormFieldType.ReversedUserCollection ||
      (field.FormFieldType == FormFieldType.ChkQuestions && field.ComponentType == TableFieldDataType.ChkQuestionsCreateEditModus);
  }

  /**
   * TODO: Backend Should fix this.
   * Function that returns a JSON string representation of the given form values.
   * It's main purpose is to replace certain keys with their lowercase counterparts.
   * Because the backend sometimes requires json keys to start with lowercase letters.
   * @param json The JSON object to stringify
   * @returns
   */
  private static stringifyFormJson(json: { [key: string]: unknown }): string {
    const parsedJson = JSON.stringify(json, (key, value) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const replacement: any = {};
        for (const k in value) {
          if (Object.hasOwnProperty.call(value, k) && k in FORM_CONSTANTS) {
            replacement[FORM_CONSTANTS[k]] = value[k];
          } else
            replacement[k] = value[k];
        }
        return replacement;
      }
      return value;
    });
    return parsedJson;
  }
}

